// plan-03 S05 / plan-10 S02: terminal wiring — input → parser → commands → streaming

import { parseCommand } from './parser'
import { getCommand, UNKNOWN_LINE } from './commands'
import type { CommandCtx } from './commands'
import { streamText, createStreamTarget, disableSkip } from './streaming'
import { mountCursor } from './cursor'
import { runBoot } from './boot'
import { mountIdleScheduler } from './idle'
import { mountStatusBar, mountTerminalViewport } from './layout'
import { resolveHashCommands } from './hash'
import { bump } from '../core/save'
import { play } from '../audio/sfx'
import { emit } from '../core/bus'
import { parseQuery } from '../core/url'
import { isShellCommand, isShellBare, tryAutoCd, dispatchShell, restoreCwd, getCwd } from '../widgets/filesystem/shell'
import { init as initFs } from '../widgets/filesystem/fs'

const MAX_LINES = 480
const SCROLL_RESUME_THRESHOLD = 8

type OracleMode = 'boot' | 'idle' | 'active' | 'divination' | 'consensus' | 'mourning'

export async function mountOracle(): Promise<void> {
  const query = parseQuery()

  // boot runs on every page load
  const shouldBoot = true

  // status bar
  const statusBar = document.getElementById('vt-status-bar')!
  mountStatusBar(statusBar, { quiet: query.quiet })

  // terminal viewport
  const mount = document.getElementById('vt-terminal-mount')!
  const { history, inputEl, form } = mountTerminalViewport(mount)

  // cursor — blink in the input form row; detach returns it there after streaming
  const cursorEl = history.parentElement?.querySelector('.vt-cursor') as HTMLElement
  const cursor = mountCursor(cursorEl, form)

  // command context
  let abortCtrl: AbortController | null = null
  const ctx: CommandCtx = {
    history,
    cursor,
    streamFn: streamText,
  }

  // mode state machine
  let mode: OracleMode = 'boot'
  let idleTimer: ReturnType<typeof setTimeout> | null = null
  let idleScheduler = mountIdleScheduler(history, cursor)

  // boot guard — prevents page reload if user submits during animation
  let pendingCommand: string | null = null
  const bootCtrl = new AbortController()
  function bootGuard(e: Event): void {
    e.preventDefault()
    const raw = inputEl.value.trim()
    inputEl.value = ''
    if (raw) {
      pendingCommand = raw
      bootCtrl.abort()
      disableSkip()
    }
  }
  form.addEventListener('submit', bootGuard)

  function setMode(newMode: OracleMode, reason: string): void {
    const old = mode
    mode = newMode
    emit('oracle.mode', { from: old, to: newMode })
    void reason
  }

  // --- plan-10 S02: bottom-anchor scroll logic (hardened) ---
  // Real-terminal behavior: while the view sits at the bottom, every new line —
  // idle chatter, streamed output, widget messages — keeps it pinned there. The
  // moment the user scrolls up into the backlog we stop following, and only
  // resume once they return to the bottom (or click the resume chip).
  let userScrolledUp = false
  let pruneNoticeShown = false
  let scrollResumeChip: HTMLElement | null = null
  let anchorRaf = 0

  function isAtBottom(): boolean {
    return history.scrollHeight - history.scrollTop - history.clientHeight <= SCROLL_RESUME_THRESHOLD
  }

  function createScrollResumeChip(): HTMLElement {
    const chip = document.createElement('div')
    chip.className = 'vt-scroll-resume'
    chip.textContent = '[ ▼ scroll to latest ]'
    chip.addEventListener('click', anchorToBottom)
    // position at bottom-right of history pane
    const wrapper = history.parentElement
    if (wrapper) {
      wrapper.style.position = 'relative'
      wrapper.appendChild(chip)
    }
    return chip
  }

  function removeScrollResumeChip(): void {
    if (scrollResumeChip) {
      scrollResumeChip.remove()
      scrollResumeChip = null
    }
  }

  // A scroll event is the only signal of user intent: at the bottom means
  // "follow the tail"; anywhere above means "let me read the backlog".
  history.addEventListener(
    'scroll',
    () => {
      if (isAtBottom()) {
        userScrolledUp = false
        removeScrollResumeChip()
      } else {
        userScrolledUp = true
        if (!scrollResumeChip) {
          scrollResumeChip = createScrollResumeChip()
        }
      }
    },
    { passive: true },
  )

  // Pin to the bottom on the next frame — unless the user has scrolled away.
  // rAF-coalesced so a burst of appends (or per-character streaming) costs at
  // most one scroll write per frame, and the userScrolledUp check runs at the
  // last moment so a mid-stream scroll-up reliably wins.
  function scheduleAnchor(): void {
    if (userScrolledUp || anchorRaf) return
    anchorRaf = requestAnimationFrame(() => {
      anchorRaf = 0
      if (!userScrolledUp) history.scrollTop = history.scrollHeight
    })
  }

  // Force the view back to the bottom and re-engage follow (resume chip click).
  function anchorToBottom(): void {
    userScrolledUp = false
    removeScrollResumeChip()
    history.scrollTop = history.scrollHeight
  }

  // Auto-follow ALL content changes so no append site has to remember to
  // anchor: new idle lines, streamed characters, and widget output all flow
  // through here, and every one of them respects the user's backlog position.
  const followObserver = new MutationObserver(scheduleAnchor)
  followObserver.observe(history, { childList: true, subtree: true, characterData: true })

  // expose scheduleAnchor globally for streaming.ts and widgets to call
  ;(window as unknown as Record<string, unknown>).__vtScheduleAnchor = scheduleAnchor

  // buffer cap enforcement with one-shot prune notice
  function capBuffer(): void {
    const lines = history.children
    if (lines.length <= MAX_LINES) return

    const excess = lines.length - MAX_LINES
    for (let i = 0; i < excess; i++) {
      history.removeChild(lines[0])
    }

    // first prune of session: show dim subscript
    if (!pruneNoticeShown) {
      pruneNoticeShown = true
      const notice = document.createElement('div')
      notice.className = 'vt-line-oracle'
      notice.style.color = 'var(--vt-green-dim)'
      notice.style.fontStyle = 'italic'
      notice.textContent = `// buffer pruned: ${excess} earlier line(s) released.`
      history.appendChild(notice)
    }
  }

  // idle timer: 8s after last input → idle
  function resetIdleTimer(): void {
    if (idleTimer !== null) {
      clearTimeout(idleTimer)
    }
    idleTimer = setTimeout(() => {
      if (mode === 'active') {
        setMode('idle', 'no input for 8s')
        idleScheduler.start()
      }
    }, 8000)
  }

  // start cursor
  cursor.start()

  // plan-16: init virtual filesystem + restore cwd
  initFs().catch(() => {})
  restoreCwd()

  // boot sequence
  if (shouldBoot) {
    setMode('boot', 'page load')
    await runBoot(history, cursor, bootCtrl.signal)
  }
  form.removeEventListener('submit', bootGuard)

  // post-boot: idle
  setMode('idle', 'boot complete')
  idleScheduler.start()
  resetIdleTimer()

  // hash-triggered commands (now includes stage commands via S05)
  const hashCmds = resolveHashCommands()
  for (const cmd of hashCmds) {
    if ('stageActivate' in cmd) {
      // direct stage activation (no oracle command echo)
      const { getStage } = await import('../widgets/stage/stage')
      const stage = getStage()
      if (stage) {
        stage.activate(cmd.stageActivate, cmd.stageArgs)
      }
    } else {
      const handler = getCommand(cmd.cmd)
      if (handler) {
        bump('events')
        const echoTarget = createStreamTarget(history, 'vt-line-input')
        const cmdText = [cmd.cmd, ...cmd.args].join(' ')
        const escaped = cmdText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        echoTarget.innerHTML =
          `<span style="color:var(--vt-king-green);font-weight:500">oracle</span>` +
          `<span style="color:var(--vt-green-dim)">@</span>` +
          `<span style="color:var(--vt-amber)">consensus</span>` +
          `<span style="color:var(--vt-green-dim)"> ~ </span>` +
          `<span style="color:var(--vt-term-green)">% </span>` +
          `<span style="color:var(--vt-king-green)">${escaped}</span>`
        await handler(ctx, cmd.args)
        capBuffer()
        scheduleAnchor()
      }
    }
  }

  // input handling
  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    const raw = inputEl.value
    inputEl.value = ''

    if (!raw.trim()) return

    // cancel any active streaming
    if (abortCtrl) {
      abortCtrl.abort()
    }
    abortCtrl = new AbortController()
    ctx.abortSignal = abortCtrl.signal

    // stop idle chatter
    idleScheduler.stop()
    if (idleTimer !== null) {
      clearTimeout(idleTimer)
    }

    setMode('active', 'user input')

    // plan-16: build prompt with cwd indicator
    const cwdDisplay = getCwd() === '/' ? '~' : `~${getCwd()}`

    // echo the user's input — colored prompt + command
    bump('events')
    const echoTarget = createStreamTarget(history, 'vt-line-input')
    const escaped = raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    echoTarget.innerHTML =
      `<span style="color:var(--vt-king-green);font-weight:500">oracle</span>` +
      `<span style="color:var(--vt-green-dim)">@</span>` +
      `<span style="color:var(--vt-amber)">consensus</span>` +
      `<span style="color:var(--vt-green-dim)"> ${cwdDisplay} </span>` +
      `<span style="color:var(--vt-term-green)">% </span>` +
      `<span style="color:var(--vt-king-green)">${escaped}</span>`
    scheduleAnchor()

    // plan-16: check shell commands first, then Oracle commands
    let handled = false
    if (isShellBare(raw)) {
      handled = await dispatchShell(raw, ctx)
    } else if (isShellCommand(raw)) {
      handled = await dispatchShell(raw, ctx)
    }

    if (!handled) {
      // parse and execute oracle command
      const parsed = parseCommand(raw)
      if (parsed) {
        const handler = getCommand(parsed.cmd)
        if (handler) {
          play('command_accept')
          cursor.speedBurst()
          await handler(ctx, parsed.args)
        } else {
          // plan-16: try zsh AUTO_CD before falling through to unknown
          handled = await tryAutoCd(raw)
          if (!handled) {
            play('command_reject')
            const target = createStreamTarget(history, 'vt-line-unknown')
            await streamText(target, UNKNOWN_LINE)
          }
        }
      }
    }

    capBuffer()
    scheduleAnchor()
    resetIdleTimer()
  })

  // Execute any command typed during boot animation
  if (pendingCommand) {
    inputEl.value = pendingCommand
    pendingCommand = null
    form.dispatchEvent(new Event('submit', { cancelable: true }))
  }

  // focus input on click anywhere in terminal
  const wrapper = history.parentElement
  if (wrapper) {
    wrapper.addEventListener('click', (e) => {
      // don't steal focus from buttons/links
      const t = e.target as HTMLElement
      if (t.tagName === 'BUTTON' || t.tagName === 'A' || t.getAttribute('role') === 'button') return
      inputEl.focus()
    })
  }

  // auto-focus input
  inputEl.focus()
}
